import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { clientsQuery } from '../api/clients.ts';
import { addCrewInduction } from '../api/compliance.ts';
import { crewQuery } from '../api/crew.ts';
import { stockLocationsQuery } from '../api/inventory.ts';
import { dayWithYear } from '../api/quotes.ts';
import {
  lapsingInductionsQuery,
  overdueInvoicesQuery,
  venueJobsQuery,
  venueQuotesQuery,
} from '../api/venue-page.ts';
import {
  contactsQuery,
  KIND_LABELS,
  RELATIONSHIP_LABELS,
  roomsQuery,
  ROLE_LABELS,
  setMine,
  type Venue,
  venuesQuery,
} from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { Popover } from '../components/popover.tsx';
import { formatMinorUnits } from '../money.ts';
import { span, weekOf } from '../venue-week.ts';
import { zoneName } from './roster-bars.tsx';

// CB-REQ VEN-01 at v1.9104, the operator's venue page design of 2026-10-26 (docs/designs/venue-page
// .md §1): the header on every tab — tags, the name with the My venue star, chips for the address,
// the venue's zone, the main contact and the debtor organisation, Quote here and Edit, and the
// stats — and under it the problem line, one red line each with its fix. Each stat and problem is
// asked for only by someone who may read it, and otherwise left out (never shown empty).

const BILLING = { resale: 'Resale', direct_bill: 'Direct bill', agency: 'Agency' } as const;

export function VenueHeader({
  venue: v,
  capabilities,
  tenantZone,
  onEdit,
  onChanged,
}: {
  venue: Venue;
  capabilities: readonly string[];
  tenantZone: string;
  onEdit: (() => void) | null;
  onChanged: () => Promise<void>;
}) {
  const may = (key: string) => capabilities.includes(key);
  const zone = v.timeZone ?? tenantZone;
  const now = Temporal.Now.instant();
  const today = now.toZonedDateTimeISO(zone).toPlainDate().toString();
  const week = weekOf(today, zone);
  const year = span(now, -365);
  const next7 = span(now, 7);

  const listed = useQuery(venuesQuery).data?.find((x) => x.id === v.id);
  const contacts = useQuery(contactsQuery(v.id));
  const rooms = useQuery(roomsQuery(v.id));
  const clients = useQuery({ ...clientsQuery, enabled: !!v.billedAsClientId });
  const jobsYear = useQuery({
    ...venueJobsQuery(v.id, year.from, year.to),
    enabled: may('jobs.view'),
  });
  const jobsWeek = useQuery({
    ...venueJobsQuery(v.id, week.from.toString(), week.to.toString()),
    enabled: may('jobs.view'),
  });
  const jobsNext = useQuery({
    ...venueJobsQuery(v.id, next7.from, next7.to),
    enabled: may('jobs.view') && may('prices.view') && may('quotes.view'),
  });
  const quotesNext = useQuery({
    ...venueQuotesQuery(v.id, next7.from, next7.to),
    enabled: may('jobs.view') && may('prices.view') && may('quotes.view'),
  });
  const stock = useQuery({ ...stockLocationsQuery, enabled: may('inventory.view') });
  const star = useMutation({
    mutationFn: (mine: boolean) => setMine(v.id, mine),
    onSuccess: onChanged,
  });

  const main =
    (contacts.data ?? []).find((c) =>
      c.roles.some((r) => r === 'venue_manager' || r === 'events_coordinator'),
    ) ?? contacts.data?.[0];
  const debtor = v.billedAsClientId
    ? clients.data?.find((c) => c.id === v.billedAsClientId)?.name
    : undefined;
  const ourGear = (stock.data ?? [])
    .filter((l) => l.venueId === v.id)
    .flatMap((l) => l.stock)
    .reduce((n, s) => n + s.onHand, 0);
  // The value of the jobs in the next 7 days: each one's quote in force (JOB-01 carries no money).
  const value = (() => {
    if (!jobsNext.data || !quotesNext.data) return null;
    const inForce = new Set(jobsNext.data.map((j) => j.quoteId));
    const totals = quotesNext.data.filter((q) => inForce.has(q.id) && q.total);
    const currency = totals[0]?.total?.currency ?? 'AUD';
    const minor = totals.reduce((n, q) => n + BigInt(q.total!.amountMinor), 0n);
    return formatMinorUnits(minor.toString(), currency);
  })();

  const stats: Array<[string, ReactNode]> = [];
  if (jobsYear.data) stats.push(['Jobs · 12 mo', jobsYear.data.length]);
  if (jobsWeek.data) stats.push(['This week', jobsWeek.data.length]);
  if (rooms.data) stats.push(['Rooms', rooms.data.length]);
  if (may('inventory.view') && v.holdsStock && stock.data) stats.push(['Our gear here', ourGear]);
  if (value !== null) stats.push(['Next 7 days', <span className="text-accent">{value}</span>]);

  return (
    <header className="flex flex-wrap items-start justify-between gap-6">
      <div className="flex min-w-0 flex-[1_1_32rem] flex-col gap-2">
        <div className="flex flex-wrap gap-1.5">
          <span className="tag tag-accent">{RELATIONSHIP_LABELS[v.relationship]}</span>
          {v.kind !== 'venue' ? <span className="tag">{KIND_LABELS[v.kind]}</span> : null}
          {listed?.billing ? (
            <span className="tag">{BILLING[listed.billing.supplyModel]}</span>
          ) : null}
          {v.holdsStock ? <span className="tag">Our stock here</span> : null}
          {v.surveyed ? <span className="tag tag-outline">Surveyed</span> : null}
        </div>
        <div className="flex items-center gap-2.5">
          <h1 className="font-heading text-[26px] font-extrabold leading-tight">{v.name}</h1>
          <Button
            variant="quiet"
            className="small-btn"
            aria-pressed={v.mine}
            aria-label="My venue"
            title="My venue"
            disabled={star.isPending}
            onClick={() => star.mutate(!v.mine)}
          >
            <span aria-hidden="true">{v.mine ? '★' : '☆'}</span>
          </Button>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-muted">
          {v.address ? <span>{v.address}</span> : null}
          <span>{zoneName(zone)}</span>
          {main ? (
            <span>
              {main.name}
              {main.roles[0] ? ` · ${ROLE_LABELS[main.roles[0]].toLowerCase()}` : ''}
            </span>
          ) : null}
          {debtor ? <span>{debtor}</span> : null}
        </div>
      </div>
      <div className="flex w-full min-w-0 flex-col gap-2.5 sm:w-auto sm:items-end">
        <div className="flex flex-wrap gap-2">
          {may('quotes.manage') ? (
            <Link
              to="/quotes"
              search={{ venue: v.id }}
              className="inline-flex items-center rounded-md bg-accent-600 px-[14px] py-2 font-heading text-sm font-bold text-white hover:bg-accent-700"
            >
              Quote here
            </Link>
          ) : null}
          {onEdit ? (
            <Button variant="quiet" onClick={onEdit}>
              Edit
            </Button>
          ) : null}
        </div>
        {stats.length > 0 ? (
          <dl className="venue-stats">
            {stats.map(([label, n]) => (
              <div key={label} className="venue-stat">
                <dd>{n}</dd>
                <dt>{label}</dt>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </header>
  );
}

/**
 * The problem line: a crew member's induction here lapsing within 30 days (CMP-05), the venue not
 * yet surveyed (its checklist, given as `survey`), and an overdue invoice (VEN-11) — each with its
 * fix.
 */
export function VenueProblems({
  venue: v,
  capabilities,
  survey,
  onChanged,
}: {
  venue: Venue;
  capabilities: readonly string[];
  survey: ReactNode;
  onChanged: () => Promise<void>;
}) {
  const may = (key: string) => capabilities.includes(key);
  const lapsing = useQuery({ ...lapsingInductionsQuery(v.id), enabled: may('crew.view') });
  const crew = useQuery({ ...crewQuery(false), enabled: may('crew.view') });
  const overdue = useQuery({ ...overdueInvoicesQuery(v.id), enabled: may('invoices.view') });
  const [renewing, setRenewing] = useState<{ crewMemberId: string; name: string } | null>(null);
  const nameOf = (id: string) => crew.data?.find((c) => c.id === id)?.displayName ?? 'Someone';
  const lines = [
    ...(lapsing.data ?? []).map((i) => (
      <Problem
        key={i.id}
        what="Induction lapses"
        detail={`${nameOf(i.crewMemberId)} · ${dayWithYear(i.expiresOn)}`}
        fix={
          may('crew.manage') ? (
            <Button
              variant="quiet"
              className="small-btn bg-white!"
              onClick={() =>
                setRenewing({ crewMemberId: i.crewMemberId, name: nameOf(i.crewMemberId) })
              }
            >
              Record renewal
            </Button>
          ) : null
        }
      />
    )),
    ...(overdue.data ?? []).map((inv) => (
      <Problem
        key={inv.id}
        what="Invoice overdue"
        detail={[inv.reference, inv.dueDate ? `due ${dayWithYear(inv.dueDate)}` : '']
          .filter(Boolean)
          .join(' · ')}
        fix={
          <Link
            to="/invoices/$invoiceId"
            params={{ invoiceId: inv.id }}
            className="rounded-md border border-divider bg-white px-2.5 py-1 text-[13px] font-bold"
          >
            Open
          </Link>
        }
      />
    )),
  ];
  if (lines.length === 0 && v.surveyed) return null;
  return (
    <section aria-label="Needs doing" className="flex flex-col gap-1.5">
      {lines}
      {v.surveyed ? null : survey}
      {renewing ? (
        <Renewal
          venue={v}
          who={renewing}
          onDone={async () => {
            setRenewing(null);
            await onChanged();
          }}
          onClose={() => setRenewing(null)}
        />
      ) : null}
    </section>
  );
}

function Problem({ what, detail, fix }: { what: string; detail: string; fix: ReactNode }) {
  return (
    <div className="problem-line">
      <span className="flex-1">
        <strong>{what}</strong> · {detail}
      </span>
      {fix}
    </div>
  );
}

/** CMP-05: a renewed induction, recorded for the person; its expiry is the database's. */
export function Renewal({
  venue,
  who,
  onDone,
  onClose,
}: {
  venue: Venue;
  who: { crewMemberId: string; name: string };
  onDone: () => Promise<void>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [doneOn, setDoneOn] = useState(Temporal.Now.plainDateISO().toString());
  const save = useMutation({
    mutationFn: () => addCrewInduction(who.crewMemberId, { venueId: venue.id, doneOn }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['venues', venue.id, 'inductions'] });
      await queryClient.invalidateQueries({ queryKey: ['crew', who.crewMemberId, 'inductions'] });
      await onDone();
    },
  });
  return (
    <Popover
      title={`Record renewal · ${who.name}`}
      subtitle={venue.induction ? venue.induction.name : undefined}
      onClose={onClose}
      footer={
        <>
          <Button disabled={save.isPending || !doneOn} onClick={() => save.mutate()}>
            Record
          </Button>
          <Button variant="quiet" onClick={onClose}>
            Back
          </Button>
        </>
      }
    >
      <Field
        label="Done on"
        type="date"
        value={doneOn}
        onChange={(e) => setDoneOn(e.currentTarget.value)}
      />
      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}
    </Popover>
  );
}
